import React, { useState } from "react"
import { VStack, Button, HStack, Input, Text, Skeleton, Stack, Box, Link } from "@chakra-ui/react"
import { Dialog as ChakraDialog } from "@chakra-ui/react"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "../utils/toast"
import { CancelButton, ConfirmButton } from "@hydrologiq/shared/content/components/Button"
import OrderNameEditor from "./OrderNameEditor"
import OrderDetailsEditor from "./OrderDetailsEditor"
import { computeActivityStatus } from "@hydrologiq/shared/utils/activities/activityStatus"
import { formatStatus } from "@hydrologiq/shared/utils/activities/status"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { utc } from "timezonecomplete"
import { useOrderActivities } from "../providers/hooks/useOrderActivities"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { validateQuantity } from "../utils/quantityValidation"

interface OrderEditProps {
  order: gqlTypes.Order
  // onOrderChanged removed - Apollo cache handles updates automatically
  enableAddActivity?: boolean
  onActivityCreated?: () => void
  onManageActivities?: () => void // Callback to expand activities row
}

type OrderEditView = { type: "main" } | { type: "edit-details" } | { type: "edit-name" }

const computeNextOrderStatus = (order: gqlTypes.Order): gqlTypes.OrderStatus | undefined => {
  switch (order.status) {
    case gqlTypes.OrderStatus.Pending:
      return gqlTypes.OrderStatus.Scheduled
    case gqlTypes.OrderStatus.Scheduled:
      return gqlTypes.OrderStatus.Started
    case gqlTypes.OrderStatus.Started:
      return gqlTypes.OrderStatus.Completed
    case gqlTypes.OrderStatus.Completed:
      return gqlTypes.OrderStatus.Billed
  }
  return undefined
}

const computeNextStatusCopy = (status: gqlTypes.OrderStatus | undefined, copy: Record<string, string>): string => {
  switch (status) {
    case gqlTypes.OrderStatus.Pending:
      return copy[COPY_KEYS.BUTTONS.SCHEDULE]
    case gqlTypes.OrderStatus.Scheduled:
      return copy[COPY_KEYS.BUTTONS.START]
    case gqlTypes.OrderStatus.Started:
      return copy[COPY_KEYS.BUTTONS.COMPLETE]
    case gqlTypes.OrderStatus.Completed:
      return copy[COPY_KEYS.BUTTONS.BILL]
    default:
      return ""
  }
}

// Format date for display
const formatDate = (isoDate: string | undefined): string => {
  if (!isoDate) return ""
  try {
    return new Date(isoDate).toLocaleDateString()
  } catch {
    return isoDate
  }
}

interface OrderEditFormProps {
  order: gqlTypes.Order
  error: string | null
  enableAddActivity: boolean
  nextOrderStatus: gqlTypes.OrderStatus | undefined
  nextOrderStatusText: string
  deletingOrder: boolean
  updatingOrderStatus: boolean
  hasStartedActivities: boolean
  loadingActivities: boolean
  activityless: boolean
  onEditName: () => void
  onEditDetails: () => void
  onManageActivities: () => void
  onComplete: () => void
  onDelete: () => void
}

const OrderEditForm = ({
  order,
  error,
  enableAddActivity,
  nextOrderStatus,
  nextOrderStatusText,
  deletingOrder,
  updatingOrderStatus,
  hasStartedActivities,
  loadingActivities,
  activityless,
  onEditName,
  onEditDetails,
  onManageActivities,
  onComplete,
  onDelete,
}: OrderEditFormProps) => {
  const isBilled = order.status === gqlTypes.OrderStatus.Billed
  const assetName = order.assets?.[0]?.name || "Not assigned"

  // When activityless is true, edit-lock is based on order status instead of activity status
  const isEditLocked = activityless
    ? order.status === gqlTypes.OrderStatus.Started || order.status === gqlTypes.OrderStatus.Completed
    : hasStartedActivities

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.INFORMATION]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.INFORMATION],
    [COPY_KEYS.HEADINGS.ORDER_ID]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_ID],
    [COPY_KEYS.HEADINGS.ORDER_QUANTITY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_QUANTITY],
    [COPY_KEYS.HEADINGS.ORDER_START]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_START],
    [COPY_KEYS.HEADINGS.ORDER_END]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_END],
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.BUTTONS.MANAGE_FULFILMENTS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.MANAGE_FULFILMENTS],
    [COPY_KEYS.BUTTONS.EDIT_ID]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.EDIT_ID],
    [COPY_KEYS.BUTTONS.EDIT_DETAILS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.EDIT_DETAILS],
    [COPY_KEYS.BUTTONS.ARCHIVE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.ARCHIVE],
    [COPY_KEYS.BUTTONS.BILL]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.BILL],
  })

  if (loadingActivities) {
    return (
      <VStack gap={4} align="stretch" width="100%">
        {/* Order Information Skeleton */}
        <VStack gap={2} align="stretch" width="100%">
          <Skeleton height="20px" width="100px" />
          <Stack gap={2}>
            <Skeleton height="16px" width="200px" />
            <Skeleton height="16px" width="180px" />
            <Skeleton height="16px" width="180px" />
            <Skeleton height="16px" width="180px" />
          </Stack>
        </VStack>

        {/* Action Buttons Skeleton */}
        <HStack justify="space-between" width="100%" alignItems="flex-start">
          <Skeleton height="40px" width="200px" />
          <VStack gap={3}>
            <Skeleton height="40px" width="200px" />
            <Skeleton height="40px" width="200px" />
          </VStack>
        </HStack>
      </VStack>
    )
  }

  return (
    <VStack gap={4} align="stretch" width="100%">
      {error && <ErrorAlert title="Error" message={error} />}

      {/* Order Information Section */}
      <VStack gap={2} align="stretch" width="100%">
        <Text fontSize="sm" fontWeight="semibold">
          {copy[COPY_KEYS.HEADINGS.INFORMATION]}
        </Text>
        <HStack gap={2} justify="space-between">
          <Text fontSize="sm">
            {copy[COPY_KEYS.HEADINGS.ORDER_ID]}: {order.name}
          </Text>
          <Link variant="primary" fontSize="sm" cursor="pointer" onClick={onEditName}>
            {copy[COPY_KEYS.BUTTONS.EDIT_ID]}
          </Link>
        </HStack>
        <VStack gap={2} align="stretch">
          <HStack gap={2} justify="space-between">
            <VStack gap={1} align="flex-start">
              <Text fontSize="sm">
                {copy[COPY_KEYS.HEADINGS.ORDER_QUANTITY]}: {order.quantity ? order.quantity.toFixed(2) : "n/a"}
              </Text>
              {activityless && (
                <>
                  <Text fontSize="sm">
                    Collected (kg): {order.quantity_collected != null ? order.quantity_collected.toFixed(2) : "—"}
                  </Text>
                  <Text fontSize="sm">
                    Consumed (kg): {order.quantity_consumed != null ? order.quantity_consumed.toFixed(2) : "—"}
                  </Text>
                </>
              )}
              <Text fontSize="sm">
                {copy[COPY_KEYS.HEADINGS.ORDER_START]}:{" "}
                {order.start ? formatDate(toUserTZ(order.start, utc()).toIsoString()) : "Not set"}
              </Text>
              <Text fontSize="sm">
                {copy[COPY_KEYS.HEADINGS.ORDER_END]}:{" "}
                {order.end ? formatDate(toUserTZ(order.end, utc()).toIsoString()) : "Not set"}
              </Text>
            </VStack>
            {!isEditLocked ? (
              <Link variant="primary" fontSize="sm" cursor="pointer" onClick={onEditDetails}>
                {copy[COPY_KEYS.BUTTONS.EDIT_DETAILS]}
              </Link>
            ) : (
              <Text fontSize="xs" color="fg.muted">
                (Cannot edit - activities started)
              </Text>
            )}
          </HStack>
        </VStack>
      </VStack>

      <HStack justify="space-between" width="100%" alignItems={"flex-start"}>
        {!activityless && (
          <VStack justify="flex-start">
            {enableAddActivity && (
              <Button
                width="200px"
                variant="hydrologiq_primary"
                onClick={onManageActivities}
                loading={deletingOrder}
                disabled={updatingOrderStatus}
              >
                {copy[COPY_KEYS.BUTTONS.MANAGE_FULFILMENTS]}
              </Button>
            )}
          </VStack>
        )}

        <VStack gap={3}>
          <Button
            width="200px"
            variant="hydrologiq_primary"
            onClick={onComplete}
            loading={updatingOrderStatus}
            disabled={isBilled || deletingOrder || nextOrderStatus === undefined}
          >
            {isBilled
              ? `${copy[COPY_KEYS.HEADINGS.ORDER]} ${copy[COPY_KEYS.BUTTONS.BILL]}ed`
              : `${nextOrderStatusText} ${copy[COPY_KEYS.HEADINGS.ORDER]}`}
          </Button>

          <Button
            width="200px"
            variant="warning"
            onClick={onDelete}
            loading={deletingOrder}
            disabled={updatingOrderStatus}
          >
            {copy[COPY_KEYS.BUTTONS.ARCHIVE]} {copy[COPY_KEYS.HEADINGS.ORDER]}
          </Button>
        </VStack>
      </HStack>
    </VStack>
  )
}

const OrderEdit: React.FC<OrderEditProps> = ({
  order,
  enableAddActivity = true,
  onActivityCreated: _onActivityCreated,
  onManageActivities,
}) => {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<OrderEditView>({ type: "main" })
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false)

  // Schedule dialog state for activityless mode
  const [scheduleOrdered, setScheduleOrdered] = useState<string>("")
  const [scheduleCollected, setScheduleCollected] = useState<string>("")
  const [scheduleErrors, setScheduleErrors] = useState<{ ordered?: string; collected?: string }>({})

  // Complete dialog state for activityless mode
  const [completeConsumed, setCompleteConsumed] = useState<string>("")
  const [completeCollected, setCompleteCollected] = useState<string>("")
  const [completeErrors, setCompleteErrors] = useState<{ consumed?: string; collected?: string }>({})

  const activityless = useGetFeature("orders_activityless")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.ORDER_ID]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_ID],
    [COPY_KEYS.HEADINGS.DETAILS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.DETAILS],
    [COPY_KEYS.HEADINGS.PERFORM_ACTIONS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.PERFORM_ACTIONS],
    [COPY_KEYS.HEADINGS.EDIT_PREFIX]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.EDIT_PREFIX],
    [COPY_KEYS.BUTTONS.SCHEDULE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.SCHEDULE],
    [COPY_KEYS.BUTTONS.START]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.START],
    [COPY_KEYS.BUTTONS.COMPLETE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.COMPLETE],
    [COPY_KEYS.BUTTONS.BILL]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.BILL],
  })

  console.log(order)
  // Fetch all activities for this order to enable quantity calculation
  const {
    allActivities,
    loading: loadingActivities,
    refetch: refetchActivities,
  } = useOrderActivities({
    orderId: order.id,
    clientSidePagination: true, // Fetch all activities for calculations
    initialPageSize: 100,
    skip: !open && !showDeleteConfirm && !showCompleteConfirm,
    cacheFirst: false,
  })
  console.log(allActivities)

  // Check if any activities are started or completed
  const hasStartedActivities = (allActivities || []).some((activity) => {
    const status = computeActivityStatus(activity)
    return status === "Started" || status === "Completed"
  })

  // Calculate quantity_consumed from activities (sum of activity quantities for completed activities)
  const calculatedQuantityConsumed = (allActivities || []).reduce((sum, activity) => {
    return sum + (Number(activity.quantity) || 0)
  }, 0)

  const [updateOrderStatus, { loading: updatingOrderStatus }] = useMutationWithEndpoint(
    gqlTypes.UpdateOrderStatusDocument,
    {
      // Refetch queries to update the table - Apollo cache normalization alone won't add to query results
      refetchQueries: ["GetOrders"],
      awaitRefetchQueries: false, // Don't wait for refetch to complete
    },
    "primary"
  )

  const [updateOrder] = useMutationWithEndpoint(gqlTypes.UpdateOrderDocument, undefined, "primary")

  const nextOrderStatus = computeNextOrderStatus(order)
  const nextOrderStatusText = computeNextStatusCopy(order.status, copy)

  const [deleteOrder, { loading: deletingOrder }] = useMutationWithEndpoint(
    gqlTypes.DeleteOrderDocument,
    {
      refetchQueries: ["GetOrders"],
      awaitRefetchQueries: false,
    },
    "primary"
  )
  // No refetch needed - eviction removes it from all queries

  const handleComplete = async (extraVariables?: Record<string, any>): Promise<boolean> => {
    let toastId: string | undefined
    const nextOrderStatusFormatted = nextOrderStatus ? formatStatus(nextOrderStatus) : ""
    try {
      setError(null)
      if (!nextOrderStatus) return false
      toastId = showLoadingToast(`Changing order status to ${nextOrderStatusFormatted}...`)

      // Defensively coerce quantity fields to numbers to prevent string values reaching GraphQL
      const coercedExtras = extraVariables ? { ...extraVariables } : undefined
      if (coercedExtras) {
        if (coercedExtras.quantity_consumed != null) {
          coercedExtras.quantity_consumed = Number(coercedExtras.quantity_consumed)
        }
        if (coercedExtras.quantity_collected != null) {
          coercedExtras.quantity_collected = Number(coercedExtras.quantity_collected)
        }
      }

      const { data } = await updateOrderStatus({
        variables: {
          order: order.id,
          status: nextOrderStatus,
          ...coercedExtras,
        },
      })

      if (data?.updateOrderStatus) {
        dismissToast(toastId)
        showSuccessToast(
          `Order ${nextOrderStatusFormatted}`,
          `Order "${order.name}" has been successfully ${nextOrderStatusFormatted.toLowerCase()}.`
        )
        // Apollo cache automatically updates the UI
        return true
      } else {
        dismissToast(toastId)
        const errorMessage = `Failed to ${nextOrderStatusFormatted.toLowerCase()}. Please try again.`
        setError(errorMessage)
        showErrorToast(`${nextOrderStatusFormatted} Failed`, errorMessage)
        return false
      }
    } catch (error: any) {
      console.error(`Error ${nextOrderStatusFormatted.toLowerCase()}:`, error)
      if (toastId) {
        dismissToast(toastId)
      }
      const errorMessage = error?.message || `Failed to ${nextOrderStatusFormatted.toLowerCase()}`
      setError(errorMessage)
      showErrorToast(`${nextOrderStatusFormatted} Failed`, errorMessage)
      return false
    }
  }

  const handleDelete = async () => {
    let toastId: string | undefined
    try {
      setError(null)
      toastId = showLoadingToast("Deleting order...")
      const { data } = await deleteOrder({
        variables: {
          id: order.id,
        },
      })

      if (data?.deleteOrder) {
        dismissToast(toastId)
        showSuccessToast(
          `${copy[COPY_KEYS.HEADINGS.ORDER]} Deleted`,
          `${copy[COPY_KEYS.HEADINGS.ORDER]} "${order.name}" has been successfully deleted.`
        )
        // Apollo cache automatically removes the deleted order from the UI
      } else {
        const errorMessage = `Failed to delete ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}. Please try again.`
        setError(errorMessage)
        showErrorToast("Deletion Failed", errorMessage)
      }
    } catch (error: any) {
      console.error(`Error deleting ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}:`, error)
      if (toastId) {
        dismissToast(toastId)
      }
      const errorMessage = error?.message || `Failed to delete ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}`
      setError(errorMessage)
      showErrorToast("Deletion Failed", errorMessage)
    }
  }

  // Reset view when dialog closes
  const handleDialogClose = () => {
    setOpen(false)
    setView({ type: "main" })
  }

  // Handle successful operations
  const handleSuccess = async () => {
    // Refetch activities to update the UI with latest data
    await refetchActivities()
    setView({ type: "main" })
  }

  // Handle cancel - return to main view
  const handleCancel = () => {
    setView({ type: "main" })
  }

  const openFulfilments = allActivities?.filter(
    (activity) =>
      activity.order?.id === order.id &&
      activity.status !== gqlTypes.ActivityStatus.Completed &&
      activity.kind === gqlTypes.ActivityKind.Fulfilment
  )
  console.log(openFulfilments, allActivities)

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <ChakraDialog.Root
        open={showDeleteConfirm}
        onOpenChange={(details) => !details.open && setShowDeleteConfirm(false)}
      >
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content maxWidth="md">
            <ChakraDialog.Header>
              <ChakraDialog.Title>Delete {copy[COPY_KEYS.HEADINGS.ORDER]}?</ChakraDialog.Title>
              <ChakraDialog.CloseTrigger onClick={() => setShowDeleteConfirm(false)} />
            </ChakraDialog.Header>

            <ChakraDialog.Body>
              <VStack gap={3} align="stretch">
                <Text>
                  Are you sure you want to{" "}
                  <Text as="span" fontWeight="bold">
                    delete "{order.name}"
                  </Text>
                  ? This action cannot be undone.
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  All associated activities and events will also be removed.
                </Text>
              </VStack>
            </ChakraDialog.Body>

            <ChakraDialog.Footer gap={3}>
              <CancelButton onClick={() => setShowDeleteConfirm(false)} disabled={deletingOrder} />
              <ConfirmButton
                onClick={async () => {
                  await handleDelete()
                  setShowDeleteConfirm(false)
                }}
                disabled={deletingOrder}
                loading={deletingOrder}
                colorPalette="red"
              >
                Delete {copy[COPY_KEYS.HEADINGS.ORDER]}
              </ConfirmButton>
            </ChakraDialog.Footer>
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </ChakraDialog.Root>
      {/* Complete Confirmation Dialog */}
      <ChakraDialog.Root
        open={showCompleteConfirm}
        onOpenChange={(details) => !details.open && setShowCompleteConfirm(false)}
      >
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content maxWidth="md">
            <ChakraDialog.Header>
              <VStack gap={2} align="stretch">
                <ChakraDialog.Title>
                  {copy[COPY_KEYS.HEADINGS.ORDER]}: {order.name}
                </ChakraDialog.Title>
                <ChakraDialog.Description>
                  Change the status of this {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}
                </ChakraDialog.Description>
              </VStack>
              <ChakraDialog.CloseTrigger onClick={() => setShowCompleteConfirm(false)} />
            </ChakraDialog.Header>

            <ChakraDialog.Body>
              <VStack gap={3} align="stretch">
                {error && <ErrorAlert title="Error" message={error} />}
                <Text>
                  Are you sure you want to{" "}
                  <Text as="span" fontWeight="bold">
                    {nextOrderStatusText.toLowerCase()}
                  </Text>{" "}
                  this {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}?
                </Text>

                {activityless && nextOrderStatus === gqlTypes.OrderStatus.Completed && (
                  <VStack gap={3} align="stretch" data-testid="complete-quantity-inputs">
                    <Field
                      label="Consumed (kg)"
                      invalid={!!completeErrors.consumed}
                      errorText={completeErrors.consumed}
                    >
                      <Input
                        type="number"
                        value={completeConsumed}
                        onChange={(e) => {
                          setCompleteConsumed(e.target.value)
                          if (completeErrors.consumed)
                            setCompleteErrors((prev) => ({ ...prev, consumed: undefined }))
                        }}
                        placeholder="0.00"
                        data-testid="complete-consumed-input"
                      />
                    </Field>

                    <HStack gap={2} align="flex-end">
                      <Field
                        label={
                          <HStack gap={1}>
                            <Text>Collected (kg)</Text>
                            <Tooltip content="For the purpose of consumption forecasting, we adjust the remaining on-site quantity before refuelling. This helps to improve accuracy.">
                              <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                width="16px"
                                height="16px"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="gray.400"
                                fontSize="xs"
                                cursor="help"
                                aria-label="Collected quantity info"
                                tabIndex={0}
                              >
                                ?
                              </Box>
                            </Tooltip>
                          </HStack>
                        }
                        invalid={!!completeErrors.collected}
                        errorText={completeErrors.collected}
                        flex={1}
                      >
                        <Input
                          type="number"
                          value={completeCollected}
                          onChange={(e) => {
                            setCompleteCollected(e.target.value)
                            if (completeErrors.collected)
                              setCompleteErrors((prev) => ({ ...prev, collected: undefined }))
                          }}
                          placeholder="0.00"
                          data-testid="complete-collected-input"
                        />
                      </Field>
                    </HStack>
                  </VStack>
                )}

                {!activityless && nextOrderStatus === gqlTypes.OrderStatus.Completed && (
                  <VStack gap={2} align="stretch">
                    <Field label="Quantity Consumed (kg)">
                      <Input
                        type="number"
                        value={calculatedQuantityConsumed}
                        readOnly
                        disabled
                      />
                    </Field>
                    <Text fontSize="xs" color="fg.muted">
                      Calculated from activities
                    </Text>
                  </VStack>
                )}

                {activityless && nextOrderStatus === gqlTypes.OrderStatus.Scheduled && (
                  <VStack gap={3} align="stretch" data-testid="schedule-quantity-inputs">
                    <Field
                      label="Ordered (kg)"
                      invalid={!!scheduleErrors.ordered}
                      errorText={scheduleErrors.ordered}
                    >
                      <Input
                        type="number"
                        value={scheduleOrdered}
                        onChange={(e) => {
                          setScheduleOrdered(e.target.value)
                          if (scheduleErrors.ordered)
                            setScheduleErrors((prev) => ({ ...prev, ordered: undefined }))
                        }}
                        placeholder="0.00"
                        data-testid="schedule-ordered-input"
                      />
                    </Field>

                    <HStack gap={2} align="flex-end">
                      <Field
                        label={
                          <HStack gap={1}>
                            <Text>Collected (kg)</Text>
                            <Tooltip content="For the purpose of consumption forecasting, we adjust the remaining on-site quantity before refuelling. This helps to improve accuracy.">
                              <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                width="16px"
                                height="16px"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="gray.400"
                                fontSize="xs"
                                cursor="help"
                                aria-label="Collected quantity info"
                                tabIndex={0}
                              >
                                ?
                              </Box>
                            </Tooltip>
                          </HStack>
                        }
                        invalid={!!scheduleErrors.collected}
                        errorText={scheduleErrors.collected}
                        flex={1}
                      >
                        <Input
                          type="number"
                          value={scheduleCollected}
                          onChange={(e) => {
                            setScheduleCollected(e.target.value)
                            if (scheduleErrors.collected)
                              setScheduleErrors((prev) => ({ ...prev, collected: undefined }))
                          }}
                          placeholder="0.00"
                          data-testid="schedule-collected-input"
                        />
                      </Field>
                    </HStack>
                  </VStack>
                )}

                {nextOrderStatus === gqlTypes.OrderStatus.Completed && openFulfilments?.length > 0 && (
                  <VStack align="stretch">
                    <Text fontWeight={"bold"}>You still have open fulfilments:</Text>
                    <Box as={"ul"} listStyleType="disc" pl={4}>
                      {openFulfilments.map((fulfillment) => (
                        <li key={fulfillment.id}>{fulfillment.name}</li>
                      ))}
                    </Box>
                  </VStack>
                )}
              </VStack>
            </ChakraDialog.Body>

            <ChakraDialog.Footer gap={3}>
              <CancelButton onClick={() => setShowCompleteConfirm(false)} disabled={updatingOrderStatus} />
              <ConfirmButton
                onClick={async () => {
                  // Handle activityless complete transition with validation
                  if (activityless && nextOrderStatus === gqlTypes.OrderStatus.Completed) {
                    // Validate inputs
                    const consumedError = validateQuantity(completeConsumed)
                    const collectedError = validateQuantity(completeCollected)

                    const newErrors: { consumed?: string; collected?: string } = {}
                    if (consumedError) newErrors.consumed = consumedError
                    if (collectedError) newErrors.collected = collectedError

                    setCompleteErrors(newErrors)

                    if (Object.keys(newErrors).length > 0) return

                    // Call updateOrderStatus with quantity_consumed and quantity_collected
                    const quantityConsumedValue = completeConsumed
                      ? parseFloat(completeConsumed)
                      : null
                    const quantityCollectedValue = completeCollected
                      ? parseFloat(completeCollected)
                      : null

                    const success = await handleComplete({
                      quantity_consumed: quantityConsumedValue,
                      quantity_collected: quantityCollectedValue,
                    })

                    if (success) {
                      setShowCompleteConfirm(false)
                    }
                    // On failure: dialog stays open, values retained, error displayed
                  // Handle activityless schedule transition with validation
                  } else if (activityless && nextOrderStatus === gqlTypes.OrderStatus.Scheduled) {
                    // Validate inputs
                    const orderedError = validateQuantity(scheduleOrdered)
                    const collectedError = validateQuantity(scheduleCollected)

                    const newErrors: { ordered?: string; collected?: string } = {}
                    if (orderedError) newErrors.ordered = orderedError
                    if (collectedError) newErrors.collected = collectedError

                    setScheduleErrors(newErrors)

                    if (Object.keys(newErrors).length > 0) return

                    // Call updateOrderStatus with quantity_collected
                    const quantityCollectedValue = scheduleCollected
                      ? parseFloat(scheduleCollected)
                      : null

                    await handleComplete({
                      quantity_collected: quantityCollectedValue,
                    })

                    // If quantity changed, also update via updateOrder
                    const newQuantity = scheduleOrdered ? parseFloat(scheduleOrdered) : null
                    if (newQuantity !== order.quantity) {
                      try {
                        await updateOrder({
                          variables: {
                            id: order.id,
                            quantity: newQuantity,
                          },
                        })
                      } catch {
                        // Status already changed, quantity update failure is non-blocking
                      }
                    }

                    setShowCompleteConfirm(false)
                  // Handle non-activityless complete transition (pass calculated quantity_consumed)
                  } else if (!activityless && nextOrderStatus === gqlTypes.OrderStatus.Completed) {
                    await handleComplete({
                      quantity_consumed: calculatedQuantityConsumed,
                    })
                    setShowCompleteConfirm(false)
                  } else {
                    await handleComplete()
                    setShowCompleteConfirm(false)
                  }
                }}
                disabled={updatingOrderStatus}
                loading={updatingOrderStatus}
                colorPalette="green"
              />
            </ChakraDialog.Footer>
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </ChakraDialog.Root>

      {/* Main Edit Dialog */}
      <Dialog
        open={open}
        setOpen={(isOpen) => {
          setOpen(isOpen)
          if (!isOpen) {
            handleDialogClose()
          }
        }}
        title={
          view.type === "main"
            ? `${copy[COPY_KEYS.HEADINGS.ORDER]}: ${order.name}`
            : view.type === "edit-details"
            ? `${copy[COPY_KEYS.HEADINGS.EDIT_PREFIX]} ${copy[COPY_KEYS.HEADINGS.ORDER]} ${
                copy[COPY_KEYS.HEADINGS.DETAILS]
              }: ${order.name}`
            : `${copy[COPY_KEYS.HEADINGS.EDIT_PREFIX]} ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}: ${order.name}`
        }
        description={
          view.type === "main"
            ? `${copy[COPY_KEYS.HEADINGS.PERFORM_ACTIONS]} ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}`
            : undefined
        }
        trigger={<Link variant="primary">{order.name}</Link>}
        loading={false}
        disabled={false}
        footer={false}
      >
        {view.type === "main" && (
          <OrderEditForm
            order={order}
            error={error}
            enableAddActivity={enableAddActivity}
            nextOrderStatus={nextOrderStatus}
            nextOrderStatusText={nextOrderStatusText}
            deletingOrder={deletingOrder}
            updatingOrderStatus={updatingOrderStatus}
            hasStartedActivities={hasStartedActivities}
            loadingActivities={loadingActivities}
            activityless={!!activityless}
            onEditName={() => setView({ type: "edit-name" })}
            onEditDetails={() => setView({ type: "edit-details" })}
            onManageActivities={() => {
              setOpen(false)
              onManageActivities?.()
            }}
            onComplete={() => {
              // Initialise schedule dialog state when opening
              if (activityless && nextOrderStatus === gqlTypes.OrderStatus.Scheduled) {
                setScheduleOrdered(order.quantity?.toString() ?? "")
                setScheduleCollected(order.quantity_collected?.toString() ?? "")
                setScheduleErrors({})
              }
              // Initialise complete dialog state when opening
              if (activityless && nextOrderStatus === gqlTypes.OrderStatus.Completed) {
                setCompleteConsumed("")
                setCompleteCollected(order.quantity_collected?.toString() ?? "")
                setCompleteErrors({})
              }
              setShowCompleteConfirm(true)
              setOpen(false)
            }}
            onDelete={() => {
              setShowDeleteConfirm(true)
              setOpen(false)
            }}
          />
        )}
        {view.type === "edit-details" && (
          <OrderDetailsEditor
            order={order}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            hasStartedActivities={hasStartedActivities}
            allActivities={allActivities}
          />
        )}
        {view.type === "edit-name" && (
          <OrderNameEditor order={order} onSuccess={handleSuccess} onCancel={handleCancel} />
        )}
      </Dialog>
    </>
  )
}

export default OrderEdit
